import {
  addLeadingSlash,
  pickFields,
  removeEmptyFields,
} from '@fundera/fundera-utilities';

export const schemas = {
  ABOUT: 'about',
  ARTICLE: 'article',
  AUTHOR: 'author',
  GENERIC: 'generic',
  HOMEPAGE: 'homepage',
};

const OPTIONAL_AUTHOR_FIELDS = [
  'alumniOf',
  'affiliation',
  'award',
  'honorificSuffix',
  'jobTitle',
  'knowsAbout',
];

/**
 * Get the Organization schema for the given properties
 *
 * @param {Object} properties page properties
 * @returns {Object[]}
 */
const getOrganizationSchema = properties => {
  const { address, logo, siteUrl, siteName, brand, socialLinks } = properties;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      logo,
      address,
      brand,
      name: siteName,
      sameAs: socialLinks,
      url: siteUrl,
    },
  ];
};

/**
 * Get the About schema for the given properties
 *
 * @param {Object} properties page properties
 * @returns {Object[]}
 */
const getAboutPageSchema = properties => {
  const { url, siteName, siteUrl } = properties;
  const organizationSchema = getOrganizationSchema(properties);
  const webSiteMarkup = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: siteName,
    url: `${siteUrl}${url}`,
  };
  return organizationSchema.concat(webSiteMarkup);
};

/**
 * Get the Homepage schema for the given properties
 *
 * @param {Object} properties page properties
 * @returns {Object[]}
 */
const getHomepageSchema = properties => {
  const { url, siteName, siteUrl } = properties;
  const organizationSchema = getOrganizationSchema(properties);
  const webSiteMarkup = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: `${siteUrl}${url}`,
  };
  return organizationSchema.concat(webSiteMarkup);
};

/**
 * Get the Article schema for the given properties
 *
 * @param {Object} properties page properties
 * @returns {Object[]} article schema
 */
const getArticleSchema = properties => {
  const {
    author,
    title,
    dateModified,
    datePublished,
    image,
    url,
    authorName,
    logo,
    siteName,
    siteUrl,
  } = properties;

  const postFeaturedImage = image || logo;
  const authorSection = {
    '@type': 'Person',
    name: authorName || (author && author.name) || null,
    ...pickFields(author, OPTIONAL_AUTHOR_FIELDS),
  };

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}${addLeadingSlash(url)}`,
      },
      headline: title,
      image: postFeaturedImage,
      author: removeEmptyFields(authorSection),
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: logo,
        },
      },
      datePublished,
      dateModified,
    },
  ];
};

/**
 * Get the Author schema for the given properties
 *
 * @param {Object} properties page properties
 * @returns {Object[]}
 */
export const getAuthorSchema = properties => {
  const {
    author,
    name,
    gravatar,
    absoluteProfile,
    profile,
    image,
    url,
    siteUrl,
    jobTitle,
    knowsAbout,
    award,
    alumniOf,
    affiliation,
    honorificSuffix,
  } = properties;

  const optionalFields = pickFields(
    {
      knowsAbout: knowsAbout || (author && author.knowsAbout) || null,
      jobTitle: jobTitle || (author && author.jobTitle) || null,
      award: award || (author && author.award) || null,
      alumniOf: alumniOf || (author && author.alumniOf) || null,
      affiliation: affiliation || (author && author.affiliation) || null,
      honorificSuffix:
        honorificSuffix || (author && author.honorificSuffix) || null,
    },
    OPTIONAL_AUTHOR_FIELDS
  );

  // Format Category
  return [
    {
      '@context': `https://schema.org`,
      '@type': 'Person',
      name: name || (author && author.name) || null,
      image: image || gravatar,
      url: absoluteProfile || profile || (url && `${siteUrl}${url}`),
      ...optionalFields,
    },
  ];
};

const SCHEMAS_MAPPING = {
  [schemas.ABOUT]: getAboutPageSchema,
  [schemas.ARTICLE]: getArticleSchema,
  [schemas.AUTHOR]: getAuthorSchema,
  [schemas.GENERIC]: getOrganizationSchema,
  [schemas.HOMEPAGE]: getHomepageSchema,
};

/**
 * Get the schema for the given page
 *
 * @param {string} schema schema to get
 * @param {Object} schemaProperties page properties
 * @returns {Object[]}
 */
export const getSchema = (schema, schemaProperties) => {
  const getter = SCHEMAS_MAPPING[schema];
  if (!getter) {
    // eslint-disable-next-line no-console
    console.error(`[Error] - Could not find schema for ${schema}.`);
    return [];
  }
  return getter(schemaProperties);
};
